import { useEffect, useMemo, useRef, useState } from "react";
import { api, apiError } from "./api";
import { getMediaTicket, useMediaTicket } from "./mediaTicket";
import { toast } from "sonner";
import { X, Camera, Loader2, Save, UserRound, Globe } from "lucide-react";
import { allTimezones, detectTz, fmtHMIn, setUserTimezone, ZONE_ABBR } from "./timeFmt";
import { BlockedUsers } from "./Safety";

const FILE_BASE = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
export const avatarUrl = (id, bust = "") =>
  `${FILE_BASE}/profile/photo/${id}?sig=${encodeURIComponent(getMediaTicket())}${bust ? `&v=${bust}` : ""}`;

const resizeSquare = (file, S = 256) => new Promise((res, rej) => {
  const img = new Image();
  img.onload = () => {
    const c = document.createElement("canvas");
    const s = Math.min(img.width, img.height);
    c.width = c.height = S;
    c.getContext("2d").drawImage(img, (img.width - s) / 2, (img.height - s) / 2, s, s, 0, 0, S, S);
    c.toBlob((b) => b ? res(new File([b], "avatar.png", { type: "image/png" })) : rej(new Error("resize failed")), "image/png", 0.9);
  };
  img.onerror = rej;
  img.src = URL.createObjectURL(file);
});

const inp = "h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 w-full";

function TzPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const zones = useMemo(() => allTimezones(), []);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase().replace(/ /g, "_");
    return s ? zones.filter((z) => z.toLowerCase().includes(s) || (ZONE_ABBR[z] || "").toLowerCase().includes(s)) : zones;
  }, [q, zones]);
  const eff = value || detectTz();
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-500 mb-1">Time zone</label>
      <button type="button" data-testid="profile-timezone-btn" onClick={() => setOpen((o) => !o)}
        className="h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm w-full flex items-center justify-between gap-2 hover:border-indigo-300 transition-colors">
        <span className="truncate flex items-center gap-1.5 text-left">
          <Globe size={13} className="text-indigo-500 shrink-0" />
          <span className="truncate">{value ? value.replace(/_/g, " ") : `Auto — ${detectTz().replace(/_/g, " ")}`}</span>
        </span>
        <span className="text-[10px] font-bold text-indigo-500 tabnum shrink-0" data-testid="profile-timezone-preview">{fmtHMIn(eff)}</span>
      </button>
      {open && (
        <div className="mt-1.5 border border-gray-200 rounded-lg shadow-lg bg-white overflow-hidden" data-testid="profile-timezone-list">
          <div className="p-2 border-b border-gray-100">
            <input autoFocus data-testid="profile-timezone-search" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search time zones… e.g. Kolkata, Qatar" className={`${inp} h-9`} />
          </div>
          <div className="max-h-44 overflow-y-auto">
            <button type="button" data-testid="profile-timezone-auto" onClick={() => { onChange(""); setOpen(false); setQ(""); }}
              className={`w-full text-left px-3 py-1.5 text-xs hover:bg-indigo-50 transition-colors ${!value ? "bg-indigo-50 font-bold text-indigo-700" : "text-gray-700"}`}>
              Auto-detect ({detectTz().replace(/_/g, " ")})
            </button>
            {filtered.slice(0, 250).map((z) => (
              <button type="button" key={z} data-testid={`tz-opt-${z}`} onClick={() => { onChange(z); setOpen(false); setQ(""); }}
                className={`w-full text-left px-3 py-1.5 text-xs hover:bg-indigo-50 transition-colors flex items-center justify-between gap-2 ${value === z ? "bg-indigo-50 font-bold text-indigo-700" : "text-gray-700"}`}>
                <span className="truncate">{z.replace(/_/g, " ")}</span>
                <span className="text-[10px] text-gray-400 tabnum shrink-0">{fmtHMIn(z)}</span>
              </button>
            ))}
            {!filtered.length && <div className="px-3 py-2 text-xs text-gray-400">No matches</div>}
          </div>
        </div>
      )}
      <p className="text-[10px] text-gray-400 mt-1">All chat & floor times will display in this zone, e.g. {fmtHMIn(eff)}.</p>
    </div>
  );
}

const FIELDS = [
  ["about", "About / status", "e.g. RTA desk, night shift lead"],
  ["phone", "Phone / extension", "+974 …"],
  ["location", "Location", "Doha HQ, 2nd floor"],
  ["language", "Preferred language", "English / العربية / हिन्दी"],
];

export default function ProfileModal({ me, onClose, onPhotoChanged }) {
  const mt = useMediaTicket();
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [bust, setBust] = useState("");
  const [presence, setPresence] = useState(me.presence_status || "active");
  const fileRef = useRef(null);
  const setAvail = (status) => {
    setPresence(status);
    api.post("/presence", { status }).catch((e) => toast.error(apiError(e, "Could not update availability")));
  };
  useEffect(() => { api.get("/profile").then((r) => setP(r.data)).catch((e) => toast.error(apiError(e))); }, []);
  const save = () => {
    setBusy(true);
    api.put("/profile", p).then(() => { setUserTimezone(p.timezone || ""); toast.success("Profile saved"); })
      .catch((e) => toast.error(apiError(e, "Could not save"))).finally(() => setBusy(false));
  };
  const pick = async (f) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) return toast.error("Please pick an image");
    setUploading(true);
    try {
      const small = await resizeSquare(f);
      const fd = new FormData();
      fd.append("file", small);
      await api.post("/profile/photo", fd);
      const v = String(Date.now());
      setBust(v); setP((x) => ({ ...x, has_photo: true }));
      onPhotoChanged?.(v);
      toast.success("Photo updated");
    } catch (e) { toast.error(apiError(e, "Upload failed")); }
    setUploading(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm flex items-center justify-center p-4" data-testid="profile-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-5 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-gray-900 flex items-center gap-2"><UserRound size={16} className="text-indigo-600" /> My profile</h3>
          <button onClick={onClose} data-testid="profile-close"><X size={16} className="text-gray-400" /></button>
        </div>
        {!p ? <div className="flex justify-center py-10 text-gray-300"><Loader2 className="animate-spin" /></div> : (<>
          <div className="flex items-center gap-4">
            <div className="relative">
              {p.has_photo && mt
                ? <img src={avatarUrl(me.id, bust)} alt="me" className="w-20 h-20 rounded-2xl object-cover shadow-md" data-testid="profile-photo" />
                : <div className="w-20 h-20 rounded-2xl brand-grad text-white text-2xl font-bold flex items-center justify-center shadow-md">{(p.full_name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}</div>}
              <button data-testid="profile-photo-btn" onClick={() => fileRef.current?.click()} disabled={uploading} title="Upload photo (2 MB max)"
                className="absolute -bottom-1.5 -right-1.5 w-8 h-8 rounded-full bg-indigo-600 text-white flex items-center justify-center shadow-lg hover:bg-indigo-700 transition-colors">
                {uploading ? <Loader2 size={13} className="animate-spin" /> : <Camera size={13} />}
              </button>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="profile-photo-input"
                onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} />
            </div>
            <div className="min-w-0">
              <div className="font-bold text-gray-900 truncate">{p.full_name}</div>
              <div className="text-xs text-gray-400 truncate">{p.email}</div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-indigo-500 mt-0.5">{(p.role || "").replace("_", " ")}{p.team ? ` · ${p.team}` : ""}</div>
            </div>
          </div>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Availability</label>
              <div className="flex items-center gap-1 bg-gray-100/80 rounded-lg p-1" data-testid="profile-presence-toggle" data-presence={presence === "away" ? "away" : "active"}>
                {[["active", "Active", "bg-emerald-500"], ["away", "Away", "bg-amber-400"]].map(([val, label, dot]) => (
                  <button key={val} type="button" data-testid={`profile-presence-${val}`} onClick={() => setAvail(val)}
                    className={`flex-1 inline-flex items-center justify-center gap-1.5 h-8 rounded-md text-xs font-bold transition-colors ${((presence === "away") ? "away" : "active") === val ? "bg-white shadow-sm text-gray-800" : "text-gray-400 hover:text-gray-600"}`}>
                    <span className={`w-2 h-2 rounded-full ${dot}`} /> {label}
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-gray-400 mt-1">Away shows an amber dot and hides you from the “online” count. It clears automatically when you send a message or reopen the app.</p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Blocked users</label>
              <BlockedUsers />
            </div>
            {FIELDS.map(([k, label, ph]) => (
              <div key={k}>
                <label className="block text-xs font-semibold text-gray-500 mb-1">{label}</label>
                <input data-testid={`profile-${k}`} value={p[k] || ""} onChange={(e) => setP({ ...p, [k]: e.target.value })} placeholder={ph} className={inp} />
              </div>
            ))}
            <TzPicker value={p.timezone || ""} onChange={(tz) => setP({ ...p, timezone: tz })} />
          </div>
          <div className="flex justify-end">
            <button data-testid="profile-save" onClick={save} disabled={busy}
              className="h-10 px-5 rounded-lg brand-grad text-white text-sm font-bold hover:opacity-90 transition-opacity disabled:opacity-40 inline-flex items-center gap-1.5">
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save
            </button>
          </div>
          <p className="text-[10px] text-gray-400">Your photo appears next to your messages in Groups. Admins can remove photos from Settings → Users & Access.</p>
        </>)}
      </div>
    </div>
  );
}

export function UserAvatar({ id, name, hasPhoto, size = "w-8 h-8", text = "text-[10px]" }) {
  const mt = useMediaTicket();
  const [err, setErr] = useState(false);
  useEffect(() => setErr(false), [mt, id]);
  if (hasPhoto && !err && mt)
    return <img src={avatarUrl(id)} alt={name || ""} onError={() => setErr(true)} className={`${size} rounded-full object-cover shrink-0 ring-1 ring-white shadow-sm`} />;
  return (
    <div className={`${size} rounded-full bg-gradient-to-br from-slate-400 to-slate-600 text-white ${text} font-bold flex items-center justify-center shrink-0`}>
      {(name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
    </div>
  );
}
